import React, { useState } from 'react';
import { FactoryDashboard } from './FactoryDashboard';
import { PipelineDetailedView } from './PipelineDetailedView';
import { usePipeline } from './usePipeline';
import { BatchImportView } from './BatchImportView';
import type { PipelineState } from './types';

import { FactoryOpsConsole } from './FactoryOpsConsole';

// Placeholder "Batch" View
const BatchView = () => <div style={{ color: '#aaa', padding: 20 }}>Batch Import View Pending Implementation</div>;

export const AdminFactoryV3 = () => {
    const [view, setView] = useState<'dashboard' | 'detail' | 'batch' | 'ops'>('dashboard');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [sortNewest, setSortNewest] = useState(true);

    // P2.1 Live Updates
    const [liveUpdates, setLiveUpdates] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(new Date());

    // Use Real Hook
    const { data, meta, loading, error, repairStep, refresh, activeJobs, diagnostics, runDrain, abandonStale, dumpQueue, rebuildThumbs, dlqEvents, reconcileBook, kickJob, runRepairCook, requeueDlq, getJobStatus, inspectBook, auditFailed, auditError, auditLoading, fetchManifest, updateManifest } = usePipeline(liveUpdates);
    const books = data || [];

    // Track refresh time
    React.useEffect(() => {
        setLastRefreshed(new Date());
    }, [data, diagnostics]);

    // Helpers
    const selectedTitle = books.find(d => d.id === selectedId);

    const handleSelect = (id: string) => {
        // Decode in case it came encoded
        const decodedId = decodeURIComponent(id);
        setSelectedId(decodedId);
        setView('detail');
    };

    const handleBack = () => {
        setSelectedId(null);
        setView('dashboard');
    };

    const handleRepairStep = (step: 'text' | 'audio' | 'art' | 'manifest', url?: string) => {
        if (!selectedId) return;
        repairStep(selectedId, step, url);
    };

    const handleRepairAll = () => {
        if (!selectedId) return;
        alert("Full Repair Pipeline pending implementation.");
    };

    // Implement adminFetch and cancelJob locally to fix ReferenceError
    const adminFetch = async (url: string, options: any = {}) => {
        const token = localStorage.getItem('X_ADMIN_TOKEN');
        if (!token) return { error: "No Admin Token" };
        try {
            const res = await fetch(url, {
                ...options,
                headers: {
                    ...options.headers,
                    'X-Admin-Token': token,
                    'Content-Type': 'application/json'
                }
            });
            if (res.status === 401) return { error: "Unauthorized" };
            return await res.json();
        } catch (e) {
            return { error: String(e) };
        }
    };

    const formatError = (e: any) => e.error || e.message || String(e);

    const cancelJob = async (jobId: string) => {
        if (!confirm(`Are you sure you want to CANCEL job ${jobId}? This will mark it failed and release locks.`)) return;
        const res = await adminFetch(`/api/factory/job/${jobId}/cancel`, { method: 'POST' });
        if (res.ok) {
            alert("Job Cancelled.");
            refresh();
        } else {
            alert("Cancel Failed: " + formatError(res));
        }
    };

    // Check for missing token error specifically
    const isMissingToken = error === "No Admin Token";

    // If missing token, block UI
    if (isMissingToken) {
        return (
            <div style={{ height: '100vh', background: '#111', color: '#ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <h2>🗝 Admin Token Required</h2>
                <p>Please authenticate in the main admin panel first.</p>
                <button onClick={() => window.location.href = '/admin'} style={{ marginTop: 20, padding: '10px 20px', background: '#e65100', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                    Go to Login
                </button>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100dvh', background: '#111', display: 'flex', flexDirection: 'column', color: '#e0e0e0', fontFamily: 'Inter, system-ui, sans-serif' }}>

            {/* MAIN APP HEADER */}
            <div style={{
                height: '50px',
                background: '#000',
                borderBottom: '1px solid #333',
                display: 'flex',
                alignItems: 'center',
                padding: '0 20px',
                justifyContent: 'space-between',
                flexShrink: 0
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ fontWeight: 'bold', color: '#fff', letterSpacing: '1px' }}>FACTORY <span style={{ color: '#e65100' }}>V3</span></div>

                    {/* TOKEN STATUS */}
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '11px', background: '#1a1a1a', padding: '4px 8px', borderRadius: '4px', border: '1px solid #333' }}>
                        <span style={{ color: '#4caf50', marginRight: '5px' }}>●</span>
                        <span style={{ color: '#ccc', marginRight: '10px' }}>Admin Token Loaded</span>
                        <button
                            onClick={() => {
                                const t = localStorage.getItem('X_ADMIN_TOKEN');
                                if (!t) alert("No Token Found");
                                else adminFetch('/api/factory/audit?limit=1').then(r => alert(r.ok ? "Token Valid ✅" : "Token Invalid ❌")).catch(e => alert("Error: " + e));
                            }}
                            style={{ background: '#333', border: 'none', color: '#fff', cursor: 'pointer', padding: '2px 6px', borderRadius: '2px', fontSize: '10px' }}>
                            TEST
                        </button>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <button
                        onClick={() => setView('dashboard')}
                        style={{
                            background: view === 'dashboard' || view === 'detail' ? '#333' : 'transparent',
                            color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer'
                        }}>
                        Dashboard
                    </button>
                    <button
                        onClick={() => setView('ops')}
                        style={{
                            background: view === 'ops' ? '#333' : 'transparent',
                            color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer'
                        }}>
                        Advanced
                    </button>
                    <button
                        onClick={() => setView('batch')}
                        style={{
                            background: view === 'batch' ? '#333' : 'transparent',
                            color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer'
                        }}>
                        Batch Import
                    </button>
                    {/* Reconcile Unknowns (P2) */}
                    <button
                        onClick={async () => {
                            if (!window.confirm("Scanning ALL unknown/partial books to Reconcile? This might take a moment.")) return;

                            // Find candidates
                            const candidates = books.filter(d =>
                                d.overall === 'unknown' ||
                                d.overall === 'broken' ||
                                d.overall === 'incomplete' ||
                                d.overall === 'missing'
                            );

                            if (candidates.length === 0) {
                                alert("No candidates found needing reconciliation.");
                                return;
                            }

                            if (!confirm(`Found ${candidates.length} candidates. Proceed?`)) return;

                            // Batch Execute
                            let processed = 0;
                            const errors: string[] = [];

                            // Sequential execution to avoid hammering worker limits
                            for (const book of candidates) {
                                processed++;
                                try {
                                    // User Feedback in Console for now, ideally localized toast or state
                                    console.log(`[Reconcile] Processing ${processed}/${candidates.length}: ${book.id}`);
                                    await reconcileBook(book.id);
                                } catch (e) {
                                    console.error(`Failed to reconcile ${book.id}`, e);
                                    errors.push(`${book.id}: ${String(e)}`);
                                }
                            }

                            alert(`Reconcile Complete.\nProcessed: ${processed}\nErrors: ${errors.length}`);
                            refresh();
                        }}
                        style={{
                            background: '#1a237e',
                            color: '#90caf9', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px'
                        }}>
                        Rescan Book ({books.filter(d => ['unknown', 'broken', 'incomplete', 'missing'].includes(d.overall)).length})
                    </button>

                    <div style={{ height: 20, width: 1, background: '#333', margin: '0 10px' }} />

                    {/* Live Toggle */}
                    <div style={{ fontSize: 10, color: '#666', marginRight: 5 }}>
                        Updated: {lastRefreshed.toLocaleTimeString()}
                    </div>
                    <button
                        onClick={() => setLiveUpdates(!liveUpdates)}
                        style={{
                            background: liveUpdates ? '#2e7d32' : '#333',
                            color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: 11
                        }}>
                        Live: {liveUpdates ? "ON" : "OFF"}
                    </button>

                    <button onClick={() => refresh('full')} title="Refresh queue & shallow cache. Deep details are fetched on-demand." style={{ marginLeft: 10, background: '#00695c', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}>Refresh</button>
                </div>
            </div>

            {/* CONTENT AREA */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

                {/* GLOBAL BANNERS - HIDDEN IN BATCH MODE */}
                {
                    view !== 'batch' && (
                        <>
                            {/* Status / Loading */}
                            {loading && books.length === 0 && (
                                <div style={{ background: '#0288d1', color: 'white', padding: '5px', fontSize: '11px', textAlign: 'center' }}>
                                    Loading Pipeline Data...
                                </div>
                            )}
                            {/* Error */}
                            {error && <div style={{ background: '#d32f2f', color: 'white', padding: '10px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                                ⚠️ API Error: {error}
                            </div>}

                            {/* Audit Partial Warning */}
                            {/* Audit Failure Warning */}
                            {auditFailed && (
                                <div style={{ background: '#d32f2f', color: '#fff', padding: '10px', fontSize: '12px', textAlign: 'center', borderBottom: '1px solid #b71c1c', fontWeight: 'bold' }}>
                                    ⚠️ Audit Failed: {auditError || "Timeout / Offline"}
                                    <div style={{ fontWeight: 'normal', fontSize: '11px', marginTop: 4 }}>
                                        Showing Queue Activity & Cached Data. Detail views may be outdated.
                                        <br />
                                        <span style={{ opacity: 0.8, fontSize: '10px' }}>Note: Direct URLs miss X-Admin-Token. Use curl -H "X-Admin-Token: ..." if verifying manually.</span>
                                    </div>
                                </div>
                            )}

                            {/* Truth Check Warning */}
                            {!loading && !auditLoading && !error && !auditFailed && data !== null && books.length === 0 && (
                                <div style={{ padding: 20 }}>
                                    <div style={{ background: '#ffa000', color: 'black', padding: '15px', textAlign: 'center', marginBottom: 20, borderRadius: 4 }}>
                                        <strong>Warning: Audit Returned 0 Books</strong><br />
                                        This means the <code>/api/factory/audit</code> endpoint returned an empty list.<br />
                                        However, we can still see active jobs below if the queue is running.
                                    </div>

                                    {/* Fallback Jobs View */}
                                    {(activeJobs.length > 0 || (diagnostics?.counts?.queued || 0) > 0) && (
                                        <div style={{ background: '#1e1e1e', border: '1px solid #444', borderRadius: 8, padding: 15 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, borderBottom: '1px solid #333', paddingBottom: 10 }}>
                                                <h3 style={{ margin: 0 }}>Active Jobs ({activeJobs.length})</h3>
                                                <button
                                                    onClick={() => setSortNewest(!sortNewest)}
                                                    style={{ background: 'transparent', border: '1px solid #555', color: '#888', borderRadius: 3, padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }}>
                                                    Sort: {sortNewest ? 'Newest' : 'Oldest'}
                                                </button>
                                            </div>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: '#ccc' }}>
                                                <thead>
                                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #555' }}>
                                                        <th style={{ padding: 8 }}>Job ID</th>
                                                        <th style={{ padding: 8 }}>Book ID</th>
                                                        <th style={{ padding: 8 }}>Status</th>
                                                        <th style={{ padding: 8 }}>Step</th>
                                                        <th style={{ padding: 8 }}>Age</th>
                                                        <th style={{ padding: 8 }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {activeJobs.slice().sort((a, b) => {
                                                        const tA = new Date(a.submittedAt || a.startedAt || 0).getTime();
                                                        const tB = new Date(b.submittedAt || b.startedAt || 0).getTime();
                                                        return sortNewest ? tB - tA : tA - tB;
                                                    }).map(job => {
                                                        const isRealJob = String(job.id).startsWith('job-');
                                                        return (
                                                            <tr key={job.id} style={{ borderBottom: '1px solid #333' }}>
                                                                <td style={{ padding: 8, fontFamily: 'monospace', color: isRealJob ? '#fff' : '#666' }}>
                                                                    {isRealJob ? job.id : '—'}
                                                                </td>
                                                                <td style={{ padding: 8, color: '#81d4fa' }}>{job.bookId || job.params?.id || 'N/A'}</td>
                                                                <td style={{ padding: 8 }}>{job.status}</td>
                                                                <td style={{ padding: 8 }}>{job.step}</td>
                                                                <td style={{ padding: 8 }}>{Math.floor((Date.now() - new Date(job.updatedAt || job.startedAt || 0).getTime()) / 1000)}s</td>
                                                                <td style={{ padding: 8 }}>
                                                                    {(job.bookId || job.params?.id) && (
                                                                        <button
                                                                            disabled={!isRealJob}
                                                                            onClick={() => handleSelect(job.bookId || job.params?.id)}
                                                                            style={{
                                                                                background: isRealJob ? '#1565c0' : '#333',
                                                                                color: isRealJob ? 'white' : '#666',
                                                                                border: 'none', padding: '2px 8px', borderRadius: 4, cursor: isRealJob ? 'pointer' : 'not-allowed'
                                                                            }}>
                                                                            View Detail
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {activeJobs.length === 0 && (
                                                        <tr>
                                                            <td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                                                                No active jobs found. Status: {diagnostics?.counts?.queued} queued, {diagnostics?.counts?.running} running.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )
                }

                {/* CONTENT AREA - Natural Scroll for Ops/Batch, Fixed for Dashboard */}
                <div style={{
                    padding: 0,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: view === 'dashboard' ? 'hidden' : 'auto'
                }}>
                    {view === 'dashboard' && books.length > 0 && <FactoryDashboard
                        data={books}
                        diagnostics={diagnostics}
                        meta={meta}
                        activeJobs={activeJobs}
                        auditLoading={auditLoading}
                        auditFailed={auditFailed}
                        onRun60s={runDrain}
                        onReconcile={reconcileBook}
                        kickJob={kickJob}
                        cancelJob={cancelJob} // P2: Passed to dashboard
                        onRepairCook={runRepairCook}
                        requeueDlq={requeueDlq}
                        rebuildThumbs={rebuildThumbs}
                        onSelectTitle={handleSelect}
                        getJobStatus={getJobStatus}
                        inspectBook={inspectBook}
                        fetchManifest={fetchManifest}
                        updateManifest={updateManifest}
                    />}
                    {view === 'detail' && selectedTitle && (
                        <PipelineDetailedView
                            title={selectedTitle as PipelineState}
                            onBack={() => setView('dashboard')}
                            onRepairStep={handleRepairStep}
                            onRepairAll={handleRepairAll}
                            onRebuildThumbs={rebuildThumbs}
                            onGetJobStatus={getJobStatus}
                        />
                    )}
                    {/* Fallback if detailed view selected but title not found (e.g. after fresh load) */}
                    {view === 'detail' && !selectedTitle && !loading && (
                        <div style={{ padding: 20, color: '#aaa' }}>
                            Title not found in audit list. <br />
                            Trying to load details for <b>{selectedId}</b>... (Implementation Pending)
                            <br /><br />
                            <button onClick={() => setView('dashboard')} style={{ color: '#90caf9', background: 'none', border: 'none', cursor: 'pointer' }}>Back to Dashboard</button>
                        </div>
                    )}

                    {view === 'batch' && <BatchImportView diagnostics={diagnostics} />}

                    {view === 'ops' && (
                        <FactoryOpsConsole
                            deployTag={meta?.deployTag || "v1.2.20-fix-ui"}
                            diagnostics={diagnostics}
                            onRun60s={runDrain}
                            onAbandonStale={abandonStale}
                            onDumpQueue={dumpQueue}
                        />
                    )}
                </div>

            </div>
        </div >
    );
};
